/**
 * Key package generation and download utilities
 * Creates a comprehensive package containing keys, metadata, and integration guide
 */

/**
 * Generate a complete key package containing all necessary information
 * @param {Object} agentData - The agent registration data
 * @param {Object} keyMaterial - The generated key material
 * @returns {Object} Complete key package
 */
export function generateKeyPackage(agentData, keyMaterial) {
  const packageData = {
    agentInfo: {
      agentId: agentData.agentId,
      agentName: agentData.profile?.name || 'Unnamed Agent',
      registrationDate: new Date().toISOString(),
      registryUrl: window.location.origin
    },
    cryptographicKeys: {
      privateKey: {
        value: keyMaterial.privateKey,
        format: 'PEM',
        type: 'Ed25519',
        description: "Keep this secret! Use for signing requests to prove your agent's identity.",
        usage: "Required for @cloudflare/web-bot-auth",
        warning: "NEVER share this private key. Store it securely."
      },
      publicJWK: {
        value: keyMaterial.publicJWK,
        format: 'JWK',
        type: 'Ed25519',
        description: "Share this with services that need to verify your agent's signatures.",
        usage: "Public key in JSON Web Key format",
        publicUrl: `${window.location.origin}/.well-known/http-message-signatures-directory/${agentData.agentId}`
      },
      keyThumbprint: {
        value: keyMaterial.thumbprint,
        format: 'base64url',
        description: "Unique fingerprint for your key pair.",
        usage: "Quick key identification and verification"
      }
    },
    integrationGuide: {
      overview: "This package contains everything needed to integrate your agent with Cloudflare's Web Bot Auth using HTTP Message Signatures.",
      quickStart: [
        "1. Follow Cloudflare's Web Bot Auth documentation for manual implementation",
        "2. Implement HTTP Message Signatures in your application",
        "3. Sign HTTP requests using your private key and agent ID",
        "4. Your requests are now cryptographically verifiable!"
      ],
      codeExample: generateSimpleCodeExample(agentData, keyMaterial),
      documentation: {
        registryResources: `${window.location.origin}?view=sdk`,
        cloudflareSDK: "https://developers.cloudflare.com/bots/concepts/bot/verified-bots/web-bot-auth/",
        testingEndpoint: "https://crawltest.com/cdn-cgi/web-bot-auth"
      },
      securityNotes: [
        "🔐 Store your private key securely using environment variables",
        "🌐 Your public key is available at the registry URL for verification",
        "⚠️ Never commit private keys to version control"
      ]
    },
    recovery: {
      registryUrl: `${window.location.origin}/.well-known/http-message-signatures-directory/${agentData.agentId}`,
      note: "If you lose these keys, you'll need to register a new agent with a different ID. Plan for key rotation and backup procedures.",
      backupRecommendations: [
        "💾 Store this file in a secure password manager (1Password, Bitwarden, etc.)",
        "☁️ Use encrypted cloud backup with access logging",
        "📋 Document your key rotation and recovery procedures",
        "🧪 Test your recovery process in a staging environment",
        "📞 Maintain emergency contact procedures for key compromise scenarios"
      ],
      keyRotation: [
        "Generate new keys using the same process as initial registration",
        "Update your application configuration with new credentials",
        "Register the new agent and verify functionality",
        "Revoke and securely delete old keys after successful migration"
      ]
    }
  };

  return packageData;
}

/**
 * Generate simple integration code example for JSON package
 * @param {Object} agentData - The agent registration data
 * @param {Object} keyMaterial - The generated key material
 * @returns {string} Simple code example
 */
function generateSimpleCodeExample(agentData, keyMaterial) {
  return `
// Manual Implementation - HTTP Message Signatures
import crypto from 'crypto';

// Your agent credentials
const AGENT_ID = '${agentData.agentId}';
const PRIVATE_KEY = process.env.WEBBOT_PRIVATE_KEY;
const REGISTRY_URL = '${window.location.origin}/.well-known/http-message-signatures-directory';

// Generate HTTP Message Signatures for requests
function signRequest(request) {
  const components = ['@authority', 'signature-agent'];
  const created = Math.floor(Date.now() / 1000);
  const expires = created + 300; // 5 minutes
  
  // Implementation based on HTTP Message Signatures spec
  // See: https://developers.cloudflare.com/bots/concepts/bot/verified-bots/web-bot-auth/
  
  return {
    'Signature': \`sig1=:\${signature}:\`,
    'Signature-Input': \`sig1=("\${components.join('" "')}");created=\${created};expires=\${expires};keyid="\${AGENT_ID}";alg="ed25519";tag="web-bot-auth"\`,
    'Signature-Agent': '"\${REGISTRY_URL}"'
  };
}

// For comprehensive documentation and examples, visit:
// ${window.location.origin}?view=sdk
  `.trim();
}



/**
 * Download the key package as a JSON file
 * @param {Object} packageData - The complete key package
 * @param {string} filename - The filename to use
 */
export function downloadKeyPackage(packageData, filename) {
  // Add metadata
  const downloadPackage = {
    ...packageData,
    _metadata: {
      version: '1.0.0',
      generator: 'Nekuda Agent Registry',
      timestamp: new Date().toISOString(),
      format: 'nekuda-agent-key-package-v1'
    }
  };

  // Convert to JSON with pretty printing
  const jsonString = JSON.stringify(downloadPackage, null, 2);
  
  // Create blob and download
  const blob = new Blob([jsonString], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  
  // Trigger download
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  // Cleanup
  URL.revokeObjectURL(url);
}

/**
 * Validate a key package structure
 * @param {Object} packageData - The package to validate
 * @returns {boolean} True if valid, throws error if invalid
 */
export function validateKeyPackage(packageData) {
  const required = [
    'agentInfo.agentId',
    'cryptographicKeys.privateKey.value',
    'cryptographicKeys.publicJWK.value',
    'cryptographicKeys.keyThumbprint.value'
  ];

  for (const path of required) {
    const value = path.split('.').reduce((obj, key) => obj?.[key], packageData);
    if (!value) {
      throw new Error(`Missing required field: ${path}`);
    }
  }

  return true;
} 